import Layout from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, Cookie, Shield, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCookieConsent } from "@/components/privacy/CookieConsent";
import { APP_NAME, PRIVACY_EMAIL } from "@/lib/constants";
import { SEO } from "@/components/SEO";

const CookiePolicy = () => {
  const { openSettings } = useCookieConsent();

  return (
    <Layout>
      <SEO 
        title="מדיניות קוקיז" 
        description={`מדיניות השימוש בקבצי עוגיות (Cookies) באתר ${APP_NAME}.`} 
      />
      <div className="bg-white min-h-screen" dir="rtl">
        <div className="border-b border-slate-100 pt-32 pb-10">
          <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
            <Link to="/" className="inline-flex items-center text-sm text-slate-400 hover:text-slate-900 mb-8 font-medium transition-colors">
              <ArrowRight className="h-4 w-4 ms-2" />
              חזרה לדף הבית
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">מדיניות קוקיז (Cookies)</h1>
            <p className="text-slate-500 mt-3">עדכון אחרון: פברואר 2026</p>
          </div>
        </div>

        <div className="container mx-auto px-6 lg:px-12 max-w-3xl py-12">
          
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-12 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-blue-600 shadow-sm">
                <Settings className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">ניהול העדפות</h3>
                <p className="text-sm text-slate-600">ניתן לשנות את הגדרות הקוקיז שלך בכל רגע.</p>
              </div>
            </div>
            <Button onClick={openSettings} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-none shrink-0 w-full sm:w-auto">
              פתח הגדרות קוקיז
            </Button>
          </div>

          <div className="prose prose-slate max-w-none prose-headings:tracking-tight prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-p:leading-relaxed prose-p:text-slate-600 prose-li:text-slate-600 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline">

            <p className="text-lg text-slate-700 font-medium">
              מסמך זה מתאר כיצד {APP_NAME} ("אנחנו") משתמשים בקבצי "Cookies" (עוגיות) וטכנולוגיות דומות באתר שלנו.
            </p>

            <h2>1. מה זה קוקיז?</h2>
            <p>
              קוקיז הם קבצי טקסט קטנים שנשמרים על המכשיר שלך (מחשב או נייד) כאשר אתה מבקר באתר אינטרנט. הם משמשים לזיהוי הדפדפן שלך, שמירת העדפות ושיפור חווית המשתמש.
            </p>

            <h2>2. סוגי הקוקיז שאנו משתמשים בהם</h2>
            
            <div className="not-prose grid gap-4 my-8">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-bold text-slate-900">קוקיז חיוניים (Strictly Necessary)</h3>
                </div>
                <p className="text-sm text-slate-600">
                  קבצים אלו הכרחיים לתפעול האתר. הם מאפשרים לך לגלוש באתר ולהשתמש בתכונות בסיסיות כמו התחברות לאזור האישי ואבטחה. ללא קבצים אלו, האתר לא יכול לפעול כראוי.
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Cookie className="w-5 h-5 text-blue-600" />
                  <h3 className="font-bold text-slate-900">קוקיז לביצועים וסטטיסטיקה</h3>
                </div>
                <p className="text-sm text-slate-600">
                  קבצים אלו אוספים מידע אנונימי על האופן שבו גולשים משתמשים באתר (למשל, אילו דפים פופולריים יותר). מידע זה עוזר לנו לשפר את האתר ואת חווית המשתמש.
                </p>
              </div>
            </div>

            <h2>3. כיצד לשלוט בקוקיז?</h2>
            <p>
              מלבד כלי הניהול המובנה שלנו (שניתן לגשת אליו בראש עמוד זה), רוב הדפדפנים מאפשרים לך לראות אילו קוקיז שמורים אצלך, למחוק אותם או לחסום אותם לחלוטין.
            </p>
            <p>
              שים לב כי מחיקת או חסימת קוקיז חיוניים עלולה לגרום לכך שחלקים מהאתר לא יעבדו כראוי.
            </p>

            <h2>4. שינויים במדיניות זו</h2>
            <p>
              אנו עשויים לעדכן את מדיניות הקוקיז מעת לעת כדי לשקף שינויים טכנולוגיים או רגולטוריים. תאריך העדכון האחרון יופיע תמיד בראש העמוד.
            </p>

            <h2>5. צור קשר</h2>
            <p>
              אם יש לך שאלות נוספות בנוגע לשימוש שלנו בקוקיז, אנא פנה אלינו בכתובת <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
            </p>

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CookiePolicy;